import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { evalSuitesService } from '../../services/eval-suites.service';
import type { EvalSuiteListParams } from '../../services/eval-suites.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { ProjectCell } from '../../components/common/project-cell';
import { useDebounce } from '../../hooks/useDebounce';
import { extractErrorMessage } from '../../utils/error-handler';
import type { EvalSuiteWithId } from '../../types';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete, useHasPermission } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import PlatformSettingsDialog from '../../components/settings/platform-settings-dialog';
import { PERMISSIONS } from '../../constants/permissions';
import { formatDate } from 'sharedUi/dateUtils';
import { StatusChip } from '../../components/common/panel';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [
  { field: 'name', header: 'Name', filter: true },
  { field: 'type', header: 'Type', filter: true },
  { field: 'suiteCode', header: 'Suite Code', filter: true },
];

const EvalList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({
    entity: 'eval',
    basePath: '/evals',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.EVALS) && !appId;
  const canEditSettings = useHasPermission(PERMISSIONS.APP_SETTING_UPDATE);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const canUpdate = useCanUpdate(JOB_NAMES.EVALS);
  const canDelete = useCanDelete(JOB_NAMES.EVALS);

  const [suites, setSuites] = useState<EvalSuiteWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSuiteId, setSelectedSuiteId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);
  // >1 accessible project ≈ platform/partner admin who may filter + re-scope.
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);

  // Pagination states for server-side pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting states for server-side sorting
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 10000 });
  }, []);

  const fetchSuites = useCallback(
    async (
      page: number = 1,
      size: number = pageSize,
      sortBy: string | null = sortField,
      order: 1 | -1 | null = sortOrder,
      activeFilters: Record<string, any> = filters
    ) => {
      try {
        setLoading(true);

        const params: EvalSuiteListParams = {
          page,
          page_size: size,
          include_deleted: includeDeleted,
        };

        if (debouncedSearchText && debouncedSearchText.trim()) {
          params.search = debouncedSearchText.trim();
        }
        if (sortBy) {
          params.sort_by = sortBy;
          params.sort_order = order === 1 ? 'asc' : 'desc';
        }
        const filtersParam = buildColumnFiltersParam(activeFilters);
        if (filtersParam) params.filters = filtersParam;

        if (selectedProjectId) params.app_id = selectedProjectId;
        params.include_global = showCore;

        const response = await evalSuitesService.list(params);
        const mappedData = (response.data || []).map((s) => ({ ...s, id: s._id }));
        setSuites(mappedData);

        const total = (response as any).pagination?.totalItems ?? (response as any).pagination?.total ?? response.total ?? 0;
        setTotalRecords(total);
      } catch (error) {
        console.error('Failed to fetch eval suites:', error);
        showError(extractErrorMessage(error, 'Failed to load eval suites'));
      } finally {
        setLoading(false);
      }
    },
    [includeDeleted, debouncedSearchText, selectedProjectId, showCore, pageSize, sortField, sortOrder, filters, showError]
  );

  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchSuites(newPage, event.rows, sortField, sortOrder);
  };

  const handleSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1 | null;
    setSortField(newSortField);
    setSortOrder(newSortOrder);
    setFirst(0);
    fetchSuites(1, pageSize, newSortField, newSortOrder);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchSuites(1, pageSize, sortField, sortOrder, next);
  };

  useEffect(() => {
    setFirst(0);
    fetchSuites(1, pageSize);
  }, [includeDeleted, debouncedSearchText, selectedProjectId, showCore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (id: string | number) => navigateToView(String(id));
  const handleEdit = (id: string | number) => navigateToEdit(String(id));

  const handleDeleteClick = (id: string | number) => {
    setSelectedSuiteId(id as string);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSuiteId) return;
    try {
      setDeleteLoading(true);
      await evalSuitesService.softDelete(selectedSuiteId);
      setDeleteDialogOpen(false);
      setSelectedSuiteId(null);
      fetchSuites(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (error: any) {
      console.error('Failed to delete eval suite:', error);
      setDeleteDialogOpen(false);
      setSelectedSuiteId(null);
      showError(extractErrorMessage(error, 'Failed to delete eval suite'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedSuiteId(null);
  };

  const columns: SaasColumn<EvalSuiteWithId>[] = useMemo(() => {
    /** 40×40 keeps every row action above the desktop hit-area floor. */
    const rowAction = 'ui-press flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700';
    const cols: SaasColumn<EvalSuiteWithId>[] = [
      { field: 'name', header: 'Name', sortable: true, filter: true, hideable: false },
      { field: 'type', header: 'Type', sortable: true, filter: true,
        body: (r) => <StatusChip tone="neutral" dot={false}>{r.type}</StatusChip> },
      { field: 'suiteCode', header: 'Suite Code', sortable: true, filter: true,
        body: (r) => <span className="font-mono text-xs text-gray-700 dark:text-gray-200">{r.suiteCode || '—'}</span> },
      { field: 'cases', header: 'Cases', align: 'right', body: (r) => <span className="tabular-nums">{r.cases?.length ?? 0}</span> },
      { field: 'version', header: 'Version', sortable: true, align: 'right', body: (r) => <span className="font-mono tabular-nums">v{r.version ?? 1}</span> },
      { field: 'createdAt', header: 'Created At', sortable: true, body: (r) => <span className="tabular-nums">{formatDate(r.createdAt)}</span> },
    ];

    cols.push({
      field: 'appId',
      header: 'Project',
      body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} />,
    });

    cols.push({
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (r) => {
        const deleted = (r as any).isDelete;
        return (
          <div className="flex justify-center gap-0.5">
            <button
              type="button"
              className={rowAction}
              onClick={() => handleView(r._id)}
              data-testid="EvalList.Action.View"
              data-pr-tooltip="View"
              aria-label="View suite"
            >
              <i className="pi pi-eye ui-act--view text-[1.05rem]" aria-hidden />
            </button>
            {!deleted && (
              <>
                {canUpdate && (
                  <button
                    type="button"
                    className={rowAction}
                    onClick={() => handleEdit(r._id)}
                    data-testid="EvalList.Action.Edit"
                    data-pr-tooltip="Edit"
                    aria-label="Edit suite"
                  >
                    <i className="pi pi-pencil ui-act--edit text-[1.05rem]" aria-hidden />
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className={rowAction}
                    onClick={() => handleDeleteClick(r._id)}
                    data-testid="EvalList.Action.Delete"
                    data-pr-tooltip="Delete"
                    aria-label="Delete suite"
                  >
                    <i className="pi pi-trash ui-act--delete text-[1.05rem]" aria-hidden />
                  </button>
                )}
              </>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [canUpdate, canDelete, canManageProjects, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.EVALS}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-EvalSuite-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-check-circle"
          title="Eval Suites"
          subtitle="Group reusable cases into suites for agents, prompts, and RAG"
          testId="EvalSuite.Header"
        />

        <div className={embedded ? '' : 'ui-panel overflow-hidden'}>
          <SaasDataTable
            tableId="eval-suite"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={suites}
            loading={loading}
            dataKey="_id"
            emptyMessage="No eval suites found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search suites..."
            testId="EvalList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px' }}
                    data-testid="EvalList.Filter.Project"
                  />
                )}
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="EvalList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={
              <>
                <span className="whitespace-nowrap text-sm tabular-nums text-ink-muted">
                  {loading && !suites.length ? 'loading\u2026' : `${totalRecords} ${totalRecords === 1 ? 'record' : 'records'}`}
                </span>
                <RefreshButton
                  onClick={() => fetchSuites(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                  testId="EvalList.Button.Refresh"
                />
                {canEditSettings && (
                  <Button label="Eval settings" icon="pi pi-sliders-h" size="small" severity="secondary" outlined
                    onClick={() => setSettingsOpen(true)} testId="EvalList.Button.Settings" />
                )}
                {canCreate && (
                  <span className="ui-press inline-flex">
                    <Button
                      label="Create Suite"
                      icon="pi pi-plus"
                      onClick={() => navigateToCreate()}
                      testId="EvalList.Button.Create"
                      size="small"
                    />
                  </span>
                )}
              </>
            }
          />
        </div>

        <PlatformSettingsDialog
          title="Eval settings"
          visible={settingsOpen}
          onHide={() => setSettingsOpen(false)}
          testId="EvalList.Dialog.Settings"
          keys={[
            { key: 'Eval_Judge_Max_Tokens', label: 'Judge output tokens', unit: 'tokens' },
            { key: 'Eval_Case_Timeout_Seconds', label: 'Case timeout', unit: 'seconds' },
            { key: 'Eval_Run_Default_Concurrency', label: 'Cases run at once' },
            { key: 'Eval_Run_Max_Tokens', label: 'Token ceiling per run', unit: 'tokens' },
            { key: 'Eval_Run_Retention_Days', label: 'Keep runs for', unit: 'days' },
          ]}
        />
        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this eval suite? This will mark it as deleted."
          testId="EvalList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default EvalList;
